import axios from "axios";
import { API_URL } from "../constants";
import { WorkSecurityListPagination } from "./work-security.service";

export type IncidentType = "incident" | "event_at_work";
export type IncidentSeverity = "none" | "lengvas" | "sunkus" | "mirtinas";
export type IncidentStatus =
  | "registered"
  | "pending_signatures"
  | "signed"
  | "closed"
  | "cancelled";

export interface IncidentWitness {
  name: string;
  notes?: string;
}

export interface IncidentDeadline {
  deadlineAt: number;
  isOverdue: boolean;
}

/**
 * List-safe projection — never includes GDPR Art. 9 medical fields.
 * NOTE (PR-2 §P3, verified against IncidentService.serializeListItem on the
 * backend): unlike roles/assignments/signed-documents, this projection does
 * NOT carry the incident's instruction area, so the "all areas" list has no
 * per-row data to render a "Sritis" column from — a backend gap, flagged for
 * follow-up. `filtersValue.areaId` (below) does correctly narrow the query
 * server-side; only the display label is missing.
 */
export interface IncidentListItem {
  id: string;
  number: string;
  type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  occurredAt: number | null;
  location: string;
  description: string;
  victimName: string;
  victimPosition: string;
  isArchived: boolean;
  createdAt: number | null;
  deadline: IncidentDeadline | null;
}

export interface IncidentSigner {
  userId: string;
  name: string;
  status: "pending" | "signed" | string;
  position: string | null;
}

export interface IncidentDocument {
  id: string;
  docType: "act" | "attachment";
  fileId: string;
  name: string;
  isSigned: boolean;
  signType: string | null;
  signers: IncidentSigner[];
  signedCount: number;
  totalCount: number;
  createdAt: number | null;
}

export interface IncidentAction {
  id: string;
  description: string;
  owner: string | null;
  ownerName: string;
  dueDate: number | null;
  doneAt: number | null;
  createdAt: number | null;
}

export interface IncidentDetail {
  id: string;
  number: string;
  type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  isSevere: boolean;
  victimUser: string | null;
  victimName: string;
  victimSnapshot: { name?: string; position?: string; hireDate?: string | null };
  managerUser: string | null;
  managerName: string;
  occurredAt: number | null;
  timeSinceShiftStart: string;
  location: string;
  description: string;
  activityBefore: string;
  causingFactor: string;
  injuryNature: string;
  bodyPart: string;
  witnesses: IncidentWitness[];
  causes: string;
  preventiveMeasures: string;
  isArchived: boolean;
  closedAt: number | null;
  cancelledAt: number | null;
  createdAt: number | null;
  deadline: IncidentDeadline | null;
  hasCurrentAct: boolean;
  currentActSigned: boolean;
  currentActSignedCount: number;
  openActionsCount: number;
  canClose: boolean;
}

export interface IncidentDetailResult {
  incident: IncidentDetail;
  actions: IncidentAction[];
  documents: IncidentDocument[];
}

export interface IncidentInput {
  type: IncidentType;
  severity?: IncidentSeverity;
  victimUser: string;
  occurredAt?: number | string | null;
  timeSinceShiftStart?: string;
  location?: string;
  description: string;
  activityBefore?: string;
  causingFactor?: string;
  injuryNature?: string;
  bodyPart?: string;
  witnesses?: IncidentWitness[];
  causes?: string;
  preventiveMeasures?: string;
  /**
   * Instruction sphere id (optional — defaults to the company's default
   * sphere). CREATE only: `update.js` does not accept/persist this field, so
   * IncidentFormModal never sends it when editing an existing incident.
   */
  area?: string;
}

export type IncidentsFetchParams = {
  page?: number;
  pageSize?: number;
  filtersValue?: {
    textSearch?: string;
    status?: IncidentStatus | "";
    type?: IncidentType | "";
    severity?: IncidentSeverity | "";
    dateFrom?: string;
    dateTo?: string;
    /**
     * Instruction area to scope the registry to (PR-2 §P1 hard rule): include
     * this key ONLY when a concrete area is selected among several — omit it
     * entirely otherwise so a single-area company's request is unchanged.
     */
    areaId?: string;
  };
  archived?: "only" | "all";
};

export type IncidentsFetchResult = {
  entries: IncidentListItem[];
  pagination: WorkSecurityListPagination;
};

const base = (companyId: string) => `${API_URL}/${companyId}/work-security/incidents`;

export async function fetchIncidents(
  companyId: string,
  params: IncidentsFetchParams
): Promise<IncidentsFetchResult> {
  const res = await axios.post(`${base(companyId)}/fetch`, params);
  return res.data;
}

export async function fetchIncidentById(
  companyId: string,
  incidentId: string
): Promise<IncidentDetailResult> {
  const res = await axios.get(`${base(companyId)}/${incidentId}`);
  return res.data;
}

export async function createIncident(
  companyId: string,
  data: IncidentInput
): Promise<{ incident: IncidentDetail }> {
  const res = await axios.post(`${base(companyId)}`, data);
  return res.data;
}

export async function updateIncident(
  companyId: string,
  incidentId: string,
  data: Partial<IncidentInput>
): Promise<{ incident: IncidentDetail }> {
  const res = await axios.patch(`${base(companyId)}/${incidentId}`, data);
  return res.data;
}

export async function setIncidentArchived(
  companyId: string,
  incidentId: string,
  isArchived: boolean
): Promise<any> {
  const res = await axios.put(`${base(companyId)}/${incidentId}/archive`, { isArchived });
  return res.data;
}

export async function generateIncidentAct(
  companyId: string,
  incidentId: string
): Promise<{ file: any }> {
  const res = await axios.post(`${base(companyId)}/${incidentId}/generate-act`);
  return res.data;
}

export async function sendIncidentForSignature(
  companyId: string,
  incidentId: string,
  managerUserId: string
): Promise<{ incident: IncidentDetail }> {
  const res = await axios.post(`${base(companyId)}/${incidentId}/send-for-signature`, {
    managerUserId,
  });
  return res.data;
}

export async function cancelIncidentSigning(
  companyId: string,
  incidentId: string
): Promise<{ incident: IncidentDetail }> {
  const res = await axios.post(`${base(companyId)}/${incidentId}/cancel-signing`);
  return res.data;
}

export async function setIncidentStatus(
  companyId: string,
  incidentId: string,
  action: "close" | "reopen" | "cancel"
): Promise<{ incident: IncidentDetail }> {
  const res = await axios.put(`${base(companyId)}/${incidentId}/status`, { action });
  return res.data;
}

export async function createIncidentAction(
  companyId: string,
  incidentId: string,
  data: { description: string; owner?: string | null; dueDate?: number | string | null }
): Promise<{ action: IncidentAction }> {
  const res = await axios.post(`${base(companyId)}/${incidentId}/actions`, data);
  return res.data;
}

export async function updateIncidentAction(
  companyId: string,
  incidentId: string,
  actionId: string,
  data: {
    description?: string;
    owner?: string | null;
    dueDate?: number | string | null;
    done?: boolean;
  }
): Promise<{ action: IncidentAction }> {
  const res = await axios.patch(`${base(companyId)}/${incidentId}/actions/${actionId}`, data);
  return res.data;
}

export async function deleteIncidentAction(
  companyId: string,
  incidentId: string,
  actionId: string
): Promise<any> {
  const res = await axios.delete(`${base(companyId)}/${incidentId}/actions/${actionId}`);
  return res.data;
}
